import { memo, useEffect, useState, type Dispatch } from "react";
import type { Observable } from "rxjs";

import Track from "./track";
import { useNetworkQualityLevelChanged } from "./local_participant";

type Dimensions = { height: number | null; width: number | null };
type Action =
  | { type: "updateDimensions"; payload: { [key: string]: Dimensions } }
  | { type: "reconnecting"; payload: string };
type VideoParticipantDispatch = Dispatch<Action> | undefined;
type RemoteParticipant = import("twilio-video").RemoteParticipant;
type RemoteTrack = import("twilio-video").RemoteTrack;
type RenderableTrack =
  | import("twilio-video").RemoteVideoTrack
  | import("twilio-video").RemoteAudioTrack;
type NetworkQualityLevel = import("twilio-video").NetworkQualityLevel | null;
type Props = {
  subscribeAudio: boolean;
  participant: RemoteParticipant;
  qualityChanged: (qualityChanged$: Observable<NetworkQualityLevel>) => void;
  videoParticipantDispatch: VideoParticipantDispatch;
  ariaLabel: string;
};

const initialState: Readonly<RenderableTrack[]> = Object.freeze([]);

function logSubscriptions(tracks: RemoteTrack[], isSubscribeAudio: boolean) {
  const logTracks = tracks.map(({ kind, name, sid }) => ({ kind, name, sid }));
  const audioQualatative = isSubscribeAudio ? "including" : "excluding";
  // eslint-disable-next-line no-console
  console.log(`Setting remote track subscriptions ${audioQualatative} audio.`, logTracks);
}

function renderableTrack(track: RemoteTrack | null): track is RemoteTrack {
  return Boolean(track);
}

function useRemoteTracks(
  participant: RemoteParticipant,
  subscribeAudio: boolean,
  videoParticipantDispatch: VideoParticipantDispatch,
) {
  const [tracks, setTracks] = useState(initialState);
  useEffect(() => {
    const setTracksCb = () => {
      const trackPublications = Array.from(participant.tracks.values());
      const filteredTracks = trackPublications
        .map((publication) => {
          if (publication.track?.kind === "video" && videoParticipantDispatch) {
            publication.on("unsubscribed", () => {
              videoParticipantDispatch({
                type: "reconnecting",
                payload: participant.identity,
              });
            });
          }
          return publication.track;
        })
        .filter(renderableTrack)
        .filter(
          (track) =>
            track.name !== "screen" &&
            (track.kind === "video" || (track.kind === "audio" && subscribeAudio)),
        )
        .map((track) => {
          if (track.kind === "video" && videoParticipantDispatch) {
            track.on("started", () => {
              videoParticipantDispatch({
                type: "updateDimensions",
                payload: { [participant.identity]: track.dimensions },
              });
            });
            track.on("dimensionsChanged", () => {
              videoParticipantDispatch({
                type: "updateDimensions",
                payload: { [participant.identity]: track.dimensions },
              });
            });
          }
          return track;
        }) as RenderableTrack[];
      logSubscriptions(filteredTracks, subscribeAudio);
      setTracks(filteredTracks);
    };
    setTracksCb(); // initial set to all tracks already subbed
    participant
      .addListener("trackSubscribed", setTracksCb)
      .addListener("trackUnsubscribed", setTracksCb);
    return () => {
      participant
        .removeListener("trackSubscribed", setTracksCb)
        .removeListener("trackUnsubscribed", setTracksCb);
    };
  }, [participant, subscribeAudio]);
  return tracks;
}

function RemoteParticipant({
  participant,
  subscribeAudio,
  qualityChanged,
  videoParticipantDispatch,
  ariaLabel,
}: Props) {
  const tracks = useRemoteTracks(participant, subscribeAudio, videoParticipantDispatch);

  useNetworkQualityLevelChanged({
    participant,
    qualityChanged,
    notifyOnDisconnect: true,
  });

  return (
    <>
      {tracks.map((track) => (
        <Track key={track.sid} track={track} ariaLabel={ariaLabel} />
      ))}
    </>
  );
}

export default memo(RemoteParticipant);
